import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
  timeoutId?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toasts: ToastMessage[] = [];
  private toastsSubject = new BehaviorSubject<ToastMessage[]>([]);
  toasts$ = this.toastsSubject.asObservable();

  show(type: 'success' | 'error' | 'info', message: string, duration: number = 3500): string {
    const id = this.generateId();
    const toast: ToastMessage = { id, type, message };
    this.toasts = [...this.toasts, toast];
    this.toastsSubject.next(this.toasts);

    if (duration > 0) {
      const timeoutId = window.setTimeout(() => {
        this.remove(id);
      }, duration);
      // Update the toast with timeoutId so we can clear it
      const updatedToast: ToastMessage = { ...toast, timeoutId };
      this.toasts = this.toasts.map(t => t.id === id ? updatedToast : t);
      this.toastsSubject.next(this.toasts);
    }

    return id;
  }

  success(message: string): string {
    return this.show('success', message);
  }

  error(message: string): string {
    return this.show('error', message);
  }

  info(message: string): string {
    return this.show('info', message);
  }

  update(id: string, message: string, type?: 'success' | 'error' | 'info'): void {
    const index = this.toasts.findIndex(t => t.id === id);
    if (index !== -1) {
      const existingToast = this.toasts[index];
      if (existingToast.timeoutId) {
        window.clearTimeout(existingToast.timeoutId);
      }
      const updatedToast: ToastMessage = { ...existingToast, message, type: type || existingToast.type };
      this.toasts[index] = updatedToast;
      this.toastsSubject.next([...this.toasts]);
    }
  }

  close() {
    this.toasts = [];
    this.toastsSubject.next([]);
  }

  remove(id: string) {
    const toast = this.toasts.find(t => t.id === id);
    if (toast && toast.timeoutId) {
      window.clearTimeout(toast.timeoutId);
    }
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.toastsSubject.next(this.toasts);
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
}
